/**
 * BillingSuccessPage — shown after successful Stripe Checkout redirect.
 *
 * - Refreshes authStore so plan_tier reflects the new plan
 * - Shows success toast
 * - If active session exists → emits billing:plan_upgraded to backend to resume game
 * - CTA: "Wróć do sesji" (if active) or "Stwórz nową grę"
 */

import { useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { useAuthStore } from "@/stores/authStore"
import { usePlayerStore } from "@/stores/playerStore"
import { useWalletStore } from "@/stores/walletStore"
import { getSocket } from "@/services/socket"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"

export function BillingSuccessPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const initAuth = useAuthStore((s) => s.initAuth)
    const refreshWallet = useWalletStore((s) => s.refresh)
    const user = useAuthStore((s) => s.user)
    const sessionCode = usePlayerStore((s) => s.sessionCode)
    const sessionUuid = usePlayerStore((s) => s.sessionUuid)
    const playerId = usePlayerStore((s) => s.playerId)

    const emittedRef = useRef(false)

    useEffect(() => {
        // Refresh user plan from backend
        initAuth().then(() => {
            toast.success(
                t("billing.planActivated"),
                { duration: 5000 }
            )
        })
        // The purchase credited coins; without this the header would keep the
        // pre-purchase balance until the next full page load.
        void refreshWallet()
    }, [initAuth, t, refreshWallet])

    useEffect(() => {
        // Emit billing:plan_upgraded if in active session (mid-game paywall resume)
        if (emittedRef.current) return
        if (!sessionUuid || !playerId) return

        const socket = getSocket()
        if (!socket?.connected) return

        emittedRef.current = true
        socket.emit("billing:plan_upgraded", {
            session_id: sessionUuid,
            player_id: playerId,
        })
    }, [sessionUuid, playerId])

    const planLabel = user?.plan_tier === "weekend_pass"
        ? t("billing.weekendPassDesc")
        : user?.plan_tier === "monthly"
            ? t("billing.monthlyDesc")
            : t("billing.newPlan")

    return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
            <Card className="bg-card border-border max-w-md w-full p-8 flex flex-col items-center gap-6">
                {/* Icon */}
                <div className="text-6xl">🎉</div>

                {/* Title */}
                <div className="text-center space-y-2">
                    <h1 className="text-primary text-2xl font-display font-bold uppercase tracking-wider">
                        {t("billing.thanks")}
                    </h1>
                    <p className="text-foreground text-sm">
                        {t("billing.planActiveText", { planLabel })}
                    </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3 w-full">
                    {sessionCode ? (
                        <Button
                            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                            onClick={() => navigate(`/session/${sessionCode}/lobby`)}
                        >
                            {t("billing.returnToSession")}
                        </Button>
                    ) : null}
                    <Button
                        variant="outline"
                        className="w-full border-border text-muted-foreground hover:bg-accent"
                        onClick={() => navigate("/")}
                    >
                        {t("billing.createNewGame")}
                    </Button>
                </div>
            </Card>
        </div>
    )
}
